import { ImageCard, MediaCard, MediaCardData } from "@components/media_card"
import { useAiAnswersPageContext } from "@contexts/main/ai_answers_page_context"
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import HistoryIcon from "@mui/icons-material/History"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import type { AiConversationMessages } from "@pages/main/ai_answers/useAiConversationMessages"
import {
  AiMessageQuestionContext,
  AiMessages,
  AiMessageStatusEnum,
  AiRunKindEnum,
  ImageMedia
} from "__generated__/graphql"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import AiAnswerProductCards from "./AiAnswerProductCards"
import {
  ANSWER_LABEL_KEYS,
  getAnswers,
  RUN_KIND_LABEL_KEYS,
  runOutcomeLabel,
  RunOutcomeTone,
  runOutcomeTone
} from "./aiMessage.utils"

export interface ConversationThread {
  id: string
  customerName: string
  channel: string
}

type ConversationMeta = Record<string, unknown> | null | undefined

function getConversationMeta(
  conversationId: AiMessages["conversationId"]
): ConversationMeta {
  if (!conversationId || typeof conversationId !== "object") return undefined

  const raw = conversationId as Record<string, unknown>
  return (raw.metadata ??
    raw.metaData ??
    raw.meta ??
    raw["conversationMeta"]) as ConversationMeta
}

function formatMetaValue(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  if (Array.isArray(value)) {
    const items = value
      .map((item) => formatMetaValue(item))
      .filter((item): item is string => Boolean(item))
    return items.length ? items.join(", ") : null
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return null
    }
  }
  return null
}

const TIMESTAMP_FORMAT = "DD.MM.YYYY HH:mm:ss"

function formatTimestamp(value: Date | string | number): string {
  return dayjs(value).format(TIMESTAMP_FORMAT)
}

/** A run is stamped when the question arrives; the reply went out once the run finished. */
function answeredAt(message: AiMessages): Date {
  const start = dayjs(message.createdAt as Date)
  return (
    message.durationMs ? start.add(message.durationMs, "ms") : start
  ).toDate()
}

function BubbleTimestamp({
  value,
  tooltip
}: {
  value: Date
  tooltip?: string
}) {
  const stamp = (
    <Typography
      component="time"
      dateTime={dayjs(value).toISOString()}
      variant="caption"
      sx={{
        display: "block",
        mt: 0.75,
        textAlign: "right",
        whiteSpace: "nowrap",
        fontSize: 11.5,
        color: "text.secondary",
        fontVariantNumeric: "tabular-nums"
      }}
    >
      {formatTimestamp(value)}
    </Typography>
  )
  return tooltip ? (
    <Tooltip title={tooltip} placement="bottom-end">
      {stamp}
    </Tooltip>
  ) : (
    stamp
  )
}

function QuestionContextCard({
  context
}: {
  context: AiMessageQuestionContext | null | undefined
}) {
  if (!context) return null
  const imageUrl = context.productImageUrl || context.postImageUrl
  const link = context.productLink || context.postUrl
  const title = context.productName || (context.postUrl ? "Gönderi" : undefined)
  if (!imageUrl && !link && !title) return null

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1,
        p: 0.75,
        borderRadius: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider"
      }}
    >
      {imageUrl && (
        <ImageCard
          media={{ path: imageUrl, type: "image/jpeg" }}
          size={{ width: 48, height: 48 }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography
            variant="caption"
            fontWeight={600}
            display="block"
            sx={{ lineHeight: 1.3 }}
          >
            {title}
          </Typography>
        )}
        {link && (
          <Link
            href={link}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              fontSize: 11,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              wordBreak: "break-all"
            }}
          >
            {context.productLink ? "Ürünü aç" : "Gönderiyi aç"}
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        )}
      </Box>
    </Box>
  )
}

const ACTION_BUTTON_SX = {
  height: 26,
  px: 1.25,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
  textTransform: "none",
  borderRadius: 1.5,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 0.5 }
} as const

function StepActions({
  message,
  isInspected,
  onInspectSteps
}: {
  message: AiMessages
  isInspected: boolean
  onInspectSteps: Props["onInspectSteps"]
}) {
  const { translate } = useAiAnswersPageContext()
  const failedCount = message.failedStepCount ?? 0

  return (
    <>
      <Button
        size="small"
        variant={isInspected ? "contained" : "outlined"}
        disableElevation
        startIcon={<AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />}
        onClick={() => onInspectSteps(message)}
        sx={ACTION_BUTTON_SX}
      >
        {translate("ai_logs_view_steps")}
      </Button>
      {failedCount > 0 && (
        <Tooltip
          title={translate("ai_logs_failed_steps_tooltip", {
            value: failedCount
          })}
          arrow
        >
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
            onClick={() => onInspectSteps(message, { onlyFailed: true })}
            sx={{ ...ACTION_BUTTON_SX, bgcolor: "rgba(211, 47, 47, 0.06)" }}
          >
            {translate("ai_logs_failed_steps", { value: failedCount })}
          </Button>
        </Tooltip>
      )}
    </>
  )
}

const RUN_TONE_COLORS: Record<RunOutcomeTone, { main: string; soft: string }> =
  {
    success: { main: "#2e7d32", soft: "#e8f5e9" },
    skipped: { main: "#b26a00", soft: "#fff4e5" },
    failed: { main: "#d32f2f", soft: "#fdecea" },
    pending: { main: "#5f6b7a", soft: "#eef1f4" }
  }

/** A follow-up or auto-close: no customer message opened it, so it reads as an event, not a turn. */
function SystemRunEvent({ message }: { message: AiMessages }) {
  const { translate } = useAiAnswersPageContext()
  const kind = message.kind as AiRunKindEnum
  const kindKey = RUN_KIND_LABEL_KEYS[kind]
  const tone = RUN_TONE_COLORS[runOutcomeTone(message)]
  const outcome = runOutcomeLabel(message.outcome, translate)
  const Icon =
    kind === AiRunKindEnum.FollowUp
      ? ScheduleOutlinedIcon
      : LockClockOutlinedIcon

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        borderLeft: `4px solid ${tone.main}`,
        bgcolor: "background.paper"
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: tone.soft,
          color: tone.main
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {translate(kindKey)}
          </Typography>
          <Typography
            component="time"
            dateTime={dayjs(message.createdAt as Date).toISOString()}
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
          >
            {formatTimestamp(message.createdAt as Date)}
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ color: tone.main }}>
          {outcome || translate("ai_logs_generating_answer")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {translate(`${kindKey}_hint`)}
        </Typography>
        {message.error && (
          <Typography variant="caption" color="error" display="block">
            {message.error}
          </Typography>
        )}
      </Box>
    </Paper>
  )
}

interface Props {
  thread: ConversationThread | null
  conversation: AiConversationMessages
  onInspectSteps: (
    message: AiMessages,
    options?: { onlyFailed?: boolean }
  ) => void
  selectedItem: AiMessages | null
  /** The turn whose step log is currently open on top of this pane, if any. */
  activeMessageId?: string | null
  /** Stays marked after the panel closes, so you can see where you were. */
  lastInspectedId?: string | null
}

export default function AiConversation({
  thread,
  conversation,
  onInspectSteps,
  selectedItem,
  activeMessageId,
  lastInspectedId
}: Props) {
  const { translate } = useAiAnswersPageContext()
  const navigate = useNavigate()

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const topRef = useRef<HTMLDivElement | null>(null)
  const prevHeightRef = useRef<number>(0)
  const didInitialScrollRef = useRef(false)
  const shouldStickToBottomRef = useRef(true)
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const prevActiveIdRef = useRef<string | null>(null)

  const {
    data,
    messages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isPending
  } = conversation

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    // If user is within 40px of bottom, we consider sticking to bottom on updates
    shouldStickToBottomRef.current = distanceFromBottom < 40
  }

  useEffect(() => {
    didInitialScrollRef.current = false
  }, [thread?.id])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const isFirstPaint = !didInitialScrollRef.current
    if (isFirstPaint || shouldStickToBottomRef.current) {
      el.scrollTop = el.scrollHeight
      didInitialScrollRef.current = true
    }
  }, [messages.length])

  useEffect(() => {
    if (!scrollRef.current || !topRef.current) return
    const rootEl = scrollRef.current

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          prevHeightRef.current = rootEl.scrollHeight
          void fetchNextPage()
        }
      },
      { root: rootEl, rootMargin: "0px 0px 200px 0px", threshold: 0 }
    )

    io.observe(topRef.current)
    return () => io.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (prevHeightRef.current > 0) {
      const diff = el.scrollHeight - prevHeightRef.current
      el.scrollTop += diff
      prevHeightRef.current = 0
    }
  }, [data?.pages.length])

  useEffect(() => {
    didInitialScrollRef.current = false
    shouldStickToBottomRef.current = true

    const el = scrollRef.current
    if (!el) return

    el.scrollTop = 0

    requestAnimationFrame(() => {
      const el2 = scrollRef.current
      if (!el2) return
      el2.scrollTop = el2.scrollHeight
    })
  }, [thread?.id])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const isFirstPaint = !didInitialScrollRef.current
    if (isFirstPaint || shouldStickToBottomRef.current) {
      el.scrollTop = el.scrollHeight
      didInitialScrollRef.current = true
    }
  }, [messages.length])

  /**
   * The step log covers this pane rather than unmounting it, so the scroll offset survives on its
   * own. All that's left is the case where the inspected turn drifted out of view meanwhile — on
   * close, bring it back so you land where you left off instead of hunting for it.
   */
  useEffect(() => {
    const closedId = prevActiveIdRef.current
    prevActiveIdRef.current = activeMessageId ?? null
    if (!closedId || activeMessageId) return

    const el = messageRefs.current.get(closedId)
    const root = scrollRef.current
    if (!el || !root) return

    const elRect = el.getBoundingClientRect()
    const rootRect = root.getBoundingClientRect()
    const isVisible =
      elRect.bottom > rootRect.top && elRect.top < rootRect.bottom
    if (isVisible) return

    shouldStickToBottomRef.current = false
    el.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [activeMessageId])

  const grouped = messages.reduce<Record<string, AiMessages[]>>((acc, m) => {
    const day = dayjs(m.createdAt as Date).format("YYYY-MM-DD")
    acc[day] = acc[day] || []
    acc[day].push(m)
    return acc
  }, {})

  const days = Object.keys(grouped).sort(
    (a, b) => dayjs(a).unix() - dayjs(b).unix()
  )

  const conversationMeta = useMemo(
    () => getConversationMeta(selectedItem?.conversationId),
    [selectedItem?.conversationId]
  )

  const metaEntries = useMemo(() => {
    if (!conversationMeta || typeof conversationMeta !== "object") return []

    return Object.entries(conversationMeta)
      .map(([key, value]) => ({
        key,
        value: formatMetaValue(value)
      }))
      .filter((entry) => entry.value)
      .slice(0, 6)
  }, [conversationMeta])

  const customerId = selectedItem?.customerId || "-"
  const conversationId = selectedItem?.conversationId?._id || "-"
  const conversationUrl =
    selectedItem?.conversationId?._id && !selectedItem.conversationId.deleted
      ? `${process.env.REACT_APP_USER_PANEL_URL}/conversations?view=all_conversations&convId=${selectedItem.conversationId._id}`
      : undefined

  const toMediaCardData = (im: ImageMedia): MediaCardData => {
    return {
      path: im.path || "",
      type: im.type || ""
    }
  }

  if (!thread) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {translate("no_conversation_selected")}
        </Typography>
      </Box>
    )
  }

  if (isLoading && !!selectedItem?.conversationId?._id) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      key={thread?.id || "no-thread"}
      ref={scrollRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        height: "100%",
        overflow: "auto",
        bgcolor: "#f6f7f9",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          px: 3,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper"
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          columnGap={3}
          rowGap={0.5}
        >
          <IdField label="Customer ID" value={customerId} />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IdField label="Conversation ID" value={conversationId} />
            {!!conversationUrl && (
              <Tooltip title={translate("ai_logs_view_conversation")}>
                <IconButton
                  size="small"
                  onClick={() => window.open(conversationUrl, "_blank")}
                  sx={{ p: 0.25 }}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        {!!metaEntries.length && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            {metaEntries.map((entry) => (
              <Chip
                key={entry.key}
                size="small"
                variant="outlined"
                label={`${entry.key}: ${entry.value}`}
              />
            ))}
          </Box>
        )}
        {(isPending || isFetching) && !!selectedItem?.conversationId?._id && (
          <LinearProgress
            sx={{ position: "absolute", left: 0, right: 0, bottom: -1 }}
          />
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 920,
          mx: "auto",
          px: 3,
          pb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1.5
        }}
      >
        <Box ref={topRef} sx={{ height: 1 }} />
        {days.map((day) => (
          <Box
            key={day}
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mt: 1.5,
                color: "text.secondary",
                "&::before, &::after": {
                  content: "''",
                  flex: 1,
                  borderTop: 1,
                  borderColor: "divider"
                }
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                {dayjs(day).locale("tr").format("D MMMM YYYY, dddd")}
              </Typography>
            </Box>

            {grouped[day].map((m) => (
              <ConversationTurn
                key={m._id}
                message={m}
                isInspected={m._id === activeMessageId}
                isLastInspected={!activeMessageId && m._id === lastInspectedId}
                onInspectSteps={onInspectSteps}
                toMediaCardData={toMediaCardData}
                registerRef={(el) => {
                  if (el) messageRefs.current.set(m._id, el)
                  else messageRefs.current.delete(m._id)
                }}
                onSimulate={() =>
                  navigate(
                    `/ai/simulation?customerId=${encodeURIComponent(
                      m.customerId
                    )}&messageId=${encodeURIComponent(m.questionId)}`
                  )
                }
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function IdField({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="caption" color="text.secondary" noWrap>
      {label}:{" "}
      <Box
        component="span"
        sx={{
          fontFamily: "monospace",
          color: "text.primary",
          userSelect: "all"
        }}
      >
        {value}
      </Box>
    </Typography>
  )
}

const BUBBLE_SX = {
  px: 1.75,
  py: 1.25,
  borderRadius: 2.5,
  maxWidth: "min(560px, 85%)",
  minWidth: 170,
  color: "text.primary",
  "& .bubble-text": {
    wordBreak: "break-word",
    overflowWrap: "break-word",
    lineHeight: 1.55
  }
} as const

function ConversationTurn({
  message: m,
  isInspected,
  isLastInspected,
  onInspectSteps,
  toMediaCardData,
  registerRef,
  onSimulate
}: {
  message: AiMessages
  isInspected: boolean
  isLastInspected: boolean
  onInspectSteps: Props["onInspectSteps"]
  toMediaCardData: (im: ImageMedia) => MediaCardData
  registerRef: (el: HTMLDivElement | null) => void
  onSimulate: () => void
}) {
  const { translate } = useAiAnswersPageContext()
  const hasFailedSteps = (m.failedStepCount ?? 0) > 0
  const answers = getAnswers(m)
  const durationLabel = m.durationMs
    ? `Run süresi: ${(m.durationMs / 1000).toFixed(1)} sn · başlangıç ${formatTimestamp(
        m.createdAt as Date
      )}`
    : undefined

  return (
    <Box
      ref={registerRef}
      sx={{
        position: "relative",
        bgcolor: isInspected ? "#eef4fd" : "background.paper",
        border: 1,
        borderColor:
          isInspected || isLastInspected ? "primary.main" : "divider",
        borderLeftWidth: 4,
        borderLeftColor:
          isInspected || isLastInspected
            ? "primary.main"
            : hasFailedSteps
              ? "rgba(211, 47, 47, 0.55)"
              : "divider",
        boxShadow: isLastInspected ? "0 0 0 3px rgba(25, 118, 210, 0.12)" : 0,
        borderRadius: 2,
        px: 2,
        pt: 1.75,
        pb: 1.25,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        transition: "background-color .2s, border-color .2s, box-shadow .2s"
      }}
    >
      {isLastInspected && (
        <Chip
          size="small"
          color="primary"
          icon={<HistoryIcon sx={{ fontSize: 14 }} />}
          label="Son incelenen"
          sx={{
            position: "absolute",
            top: -11,
            right: 12,
            height: 22,
            fontSize: 11,
            fontWeight: 600
          }}
        />
      )}
      {m.kind && <SystemRunEvent message={m} />}
      {(!!m.question || !!m.questionMedia?.length || !!m.questionContext) && (
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Paper elevation={0} sx={{ ...BUBBLE_SX, bgcolor: "#eceef1" }}>
            <QuestionContextCard context={m.questionContext} />
            {(m.questionMedia ?? [])
              .filter((x): x is ImageMedia => x != null)
              .map((im, idx) => (
                <MediaCard
                  key={im.path ?? idx}
                  // eslint-disable-next-line
                  media={toMediaCardData(im)}
                  size={{ width: 48, height: 48 }}
                />
              ))}
            <Typography
              className="bubble-text"
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: (m.question || "").replace(/\n/g, "<br />")
              }}
            />
            {m.error && !answers.length && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {m.error}
              </Typography>
            )}
            <BubbleTimestamp value={dayjs(m.createdAt as Date).toDate()} />
          </Paper>
        </Box>
      )}
      {!m.kind && m.status === AiMessageStatusEnum.Running && !m.answer && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Chip
            icon={
              <CircularProgress
                size={14}
                thickness={6}
                sx={{ color: "#8a6d00" }}
              />
            }
            label={translate("ai_logs_generating_answer")}
            sx={{
              bgcolor: "#fff3c4",
              color: "#8a6d00",
              fontWeight: 700,
              border: "1px solid #f0c419",
              "@keyframes aiPendingPulse": {
                "0%": { opacity: 0.55 },
                "50%": { opacity: 1 },
                "100%": { opacity: 0.55 }
              },
              animation: "aiPendingPulse 1.4s ease-in-out infinite"
            }}
          />
        </Box>
      )}
      {answers.map((answer, answerIdx) => (
        <Box
          key={`${m._id}-${answer.messageType ?? "answer"}-${answerIdx}`}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Paper elevation={0} sx={{ ...BUBBLE_SX, bgcolor: "#e3eefc" }}>
            {!!answer.messageType &&
              !!ANSWER_LABEL_KEYS[answer.messageType] && (
                <Chip
                  size="small"
                  label={translate(
                    ANSWER_LABEL_KEYS[answer.messageType] as string
                  )}
                  sx={{
                    mb: 0.75,
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: "#c9e0fb",
                    color: "#0b3d70"
                  }}
                />
              )}
            <Typography
              className="bubble-text"
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: (answer.content || "").replace(/\n/g, "<br />")
              }}
            />
            <AiAnswerProductCards
              cards={answer.productCards ?? []}
              title={translate("ai_logs_answer_product_cards")}
              emptyLabel={translate("ai_logs_answer_product_card_no_image")}
            />
            {m.error && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {m.error}
              </Typography>
            )}
            <BubbleTimestamp value={answeredAt(m)} tooltip={durationLabel} />
          </Paper>
        </Box>
      ))}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          pt: 1,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <StepActions
          message={m}
          isInspected={isInspected}
          onInspectSteps={onInspectSteps}
        />
        {!m.kind && (
          <>
            <Tooltip title={translate("ai_logs_quick_simulate")}>
              <Button
                size="small"
                variant="text"
                startIcon={<ScienceOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={onSimulate}
                sx={ACTION_BUTTON_SX}
              >
                {translate("ai_logs_quick_simulate")}
              </Button>
            </Tooltip>
            <Tooltip title={m.questionId}>
              <Typography
                variant="caption"
                color="text.disabled"
                noWrap
                sx={{
                  ml: "auto",
                  minWidth: 0,
                  fontFamily: "monospace",
                  fontSize: 10.5
                }}
              >
                {translate("ai_logs_message_id")}: {m.questionId}
              </Typography>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  )
}
